import React from 'react';

import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import Box from '@/assets/apideck/Box.jpg';
import Dropbox from '@/assets/apideck/Dropbox.png';
import OneDrive from '@/assets/apideck/OneDrive.png';
import SharePoint from '@/assets/apideck/SharePoint.png';
import relativityLogo from '@/assets/relativity.png';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { CustomFilePicker } from '@/new/components/apideck/apideck-file-picker';
import { ImagesButton } from '@/new/components/images-button';
import { ApideckConnection } from '@/types';

interface IntegrationBarProps {
  matterId: string;
  isApideckEnabled?: boolean;
  apiDeckConnections: ApideckConnection[];
  apiDeckSessionToken: string | null;
  onRelativityConnect: () => void;
  onApideckModal: (connection: string, modalContent: React.ReactNode) => void;
  onApideckSuccess?: () => void;
  unpackEmailRef: React.MutableRefObject<boolean>;
}

export function IntegrationBar({
  matterId,
  isApideckEnabled,
  apiDeckConnections,
  apiDeckSessionToken,
  onRelativityConnect,
  onApideckModal,
  onApideckSuccess,
  unpackEmailRef,
}: IntegrationBarProps) {
  const { user } = useUserContext();

  const isRelativityFullySetup = !!(
    user?.organisation.relativitySite &&
    user?.organisation.relativityClientId &&
    user?.organisation.relativityClientSecretEncrypted
  );

  const apideckConnectionsImages = [
    { name: 'Box', icon: Box },
    { name: 'Dropbox', icon: Dropbox },
    { name: 'OneDrive', icon: OneDrive },
    { name: 'SharePoint', icon: SharePoint },
  ];

  const handleOpenApideckModal = async (connection: string) => {
    if (!apiDeckSessionToken || !matterId) return;

    const modalContent = (
      <CustomFilePicker
        token={apiDeckSessionToken}
        matterId={matterId}
        closeModalFunction={() => {}} // This will be handled by the parent
        initialConnection={connection}
        onApideckSuccess={onApideckSuccess}
        unpackEmailRef={unpackEmailRef}
      />
    );

    onApideckModal(connection, modalContent);
  };

  return (
    <div className="flex flex-wrap justify-center gap-1.5 border-b border-gray-100 pb-4">
      {isApideckEnabled && (
        <>
          {apiDeckConnections.length
            ? apiDeckConnections.map((connection) => (
                <ImagesButton
                  key={connection.id}
                  title={connection.name}
                  imageSource={[connection.icon]}
                  hoverText={connection.name}
                  onClick={async () => await handleOpenApideckModal(connection.service_id)}
                />
              ))
            : apideckConnectionsImages.map(({ name, icon }, index) => (
                <ImagesButton
                  key={index}
                  title={name}
                  imageSource={[icon]}
                  hoverText={name}
                  onClick={() => null}
                  blankCount={1}
                />
              ))}
        </>
      )}

      {isRelativityFullySetup && (
        <PermissionGate
          permission={Permission.RelativityImport}
          uiMode="disabled"
          disabledMessage="You do not have permission to import from Relativity"
        >
          <ImagesButton
            key="relativity"
            imageSource={[relativityLogo]}
            title="Relativity"
            hoverText="Import from Relativity"
            onClick={onRelativityConnect}
            blankCount={1}
          />
        </PermissionGate>
      )}
    </div>
  );
}
